import { titles, TitleItem, TitleType } from "@/mocks/titles";
import { newsList, NewsItem } from "@/mocks/news";
import { specials, SpecialItem } from "@/mocks/specials";

export const typeLabel = (type: TitleType) => (type === "movie" ? "电影" : "电视剧");

export const getTitleById = (id: string): TitleItem | undefined =>
  titles.find((item) => item.id === id);

export const getNewsById = (id: string): NewsItem | undefined =>
  newsList.find((item) => item.id === id);

export const getSpecialById = (id: string): SpecialItem | undefined =>
  specials.find((item) => item.id === id);

export interface TitleFilters {
  region?: string;
  year?: string;
  genre?: string;
  tag?: string;
  minRating?: number;
}

export type SortKey = "updated" | "rating" | "heat";

export const filterTitles = (
  type: TitleType,
  filters: TitleFilters,
  sort: SortKey
): TitleItem[] => {
  const list = titles.filter((item) => {
    if (item.type !== type) return false;
    if (filters.region && item.region !== filters.region) return false;
    if (filters.year && String(item.year) !== filters.year) return false;
    if (filters.genre && !item.genres.includes(filters.genre)) return false;
    if (filters.tag && !item.tags.includes(filters.tag)) return false;
    if (typeof filters.minRating === "number" && item.rating < filters.minRating) return false;
    return true;
  });

  return [...list].sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "heat") return b.heat - a.heat;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
};

export const paginate = <T,>(list: T[], page: number, pageSize: number) => {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    page: safePage,
    totalPages,
    total: list.length,
  };
};

export const getRelatedTitles = (current: TitleItem, limit = 4): TitleItem[] => {
  const scored = titles
    .filter((item) => item.id !== current.id)
    .map((item) => {
      let score = 0;
      score += item.genres.filter((g) => current.genres.includes(g)).length * 10;
      if (item.type === current.type) score += 2;
      if (item.region === current.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);

  return scored.slice(0, limit).map((entry) => entry.item);
};

export const searchContent = (keyword: string) => {
  const q = keyword.trim().toLowerCase();
  if (!q) {
    return {
      titles: [] as TitleItem[],
      news: [] as NewsItem[],
      specials: [] as SpecialItem[],
      total: 0,
    };
  }

  const matchedTitles = titles.filter((item) =>
    [item.title, item.en, item.region, item.director, ...item.genres, ...item.tags]
      .join(" ")
      .toLowerCase()
      .includes(q)
  );

  const matchedNews = newsList.filter((item) =>
    [item.title, item.excerpt, item.category, item.author].join(" ").toLowerCase().includes(q)
  );

  const matchedSpecials = specials.filter((item) =>
    [item.title, item.subtitle, item.intro].join(" ").toLowerCase().includes(q)
  );

  return {
    titles: matchedTitles,
    news: matchedNews,
    specials: matchedSpecials,
    total: matchedTitles.length + matchedNews.length + matchedSpecials.length,
  };
};

export const latestTitles = (limit = 8) =>
  [...titles].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, limit);

export const hotTitles = (limit = 6) =>
  [...titles].sort((a, b) => b.heat - a.heat).slice(0, limit);

export const allTags = ["高口碑", "悬念反转", "年度剧王", "治愈", "视觉系", "群像", "燃", "质感"];